import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { CredentialsService, throwOnGraphqlError } from '@app/core';
import { MfaEnum } from '@app/models';
import { AppService } from '@app/services/app.service';
import { GenerateTotpGQL, EnableTotpGQL, EnableTotpMutationVariables } from 'src/generated/graphql.default';
import { VerifyTotpGQL, VerifyTotpMutationVariables, DisableTotpGQL, DisableTotpMutationVariables } from 'src/generated/graphql.mfa';

@Injectable({ providedIn: 'root' })
export class MfaService {
  constructor(
    private credentialsService: CredentialsService,
    private appService: AppService,
    private verifyTotpGQL: VerifyTotpGQL,
    private generateTotpGQL: GenerateTotpGQL,
    private enableTotpGQL: EnableTotpGQL,
    private disableTotpGQL: DisableTotpGQL
  ) {}

  verifyTOTP(variables: VerifyTotpMutationVariables): Observable<void> {
    return this.verifyTotpGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.verifyTOTP),
      tap(mfaAccepted =>
        mfaAccepted && this.credentialsService.credentials
          ? this.credentialsService.setCredentials({ ...this.credentialsService.credentials, mfaAccepted })
          : this.credentialsService.clearCredentials()
      ),
      switchMap(() => (this.credentialsService.credentials.mfaAccepted ? of(null) : throwError(this.appService.mockMessageValidationError('totp_code_invalid'))))
    );
  }

  generateTOTP() {
    return this.generateTotpGQL.mutate({}).pipe(
      throwOnGraphqlError(),
      map(response => response.data.generateTOTP)
    );
  }

  enableTOTP(variables: EnableTotpMutationVariables) {
    return this.enableTotpGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.enableTOTP),
      tap(() => this.credentialsService.setCredentials({ ...this.credentialsService.credentials, mfa: this.credentialsService.credentials.mfa.concat([MfaEnum.Totp]), mfaAccepted: true }))
    );
  }

  disableTOTP(variables: DisableTotpMutationVariables) {
    return this.disableTotpGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.disableTOTP),
      switchMap(response =>
        !this.credentialsService.credentials
          ? of(response)
          : this.credentialsService.apolloCacheClear().pipe(
              tap(() => this.credentialsService.setCredentials({ ...this.credentialsService.credentials, mfa: response.mfa, mfaAccepted: false })),
              map(() => response)
            )
      )
    );
  }
}
